import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useRef } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Play, Eye, Bot, Clock, Bookmark, Trash2, Save } from "lucide-react";
import {
  nlpProvider,
  httpNlpProvider,
  httpOpenAiProvider,
} from "@/lib/nlpProvider";
import { queryClient } from "@/lib/queryClient";
import { NLPIntent, NLPResult } from "@/lib/nlpProvider";
import { useBreadcrumbs } from "./BreadcrumbContext";

interface QueryHistoryItem {
  id: string;
  query: string;
  intent: NLPIntent;
  result: NLPResult;
  timestamp: string;
}

interface SavedQuery {
  id: string;
  name: string;
  query: string;
  preview: string;
}

export function AIConsole() {
  const [query, setQuery] = useState("");
  const [provider, setProvider] = useState("OpenAI");
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentIntent, setCurrentIntent] = useState<NLPIntent | null>(null);
  const [currentResult, setCurrentResult] = useState<NLPResult | null>(null);
  const [queryHistory, setQueryHistory] = useState<QueryHistoryItem[]>([]);
  const [savedQueries, setSavedQueries] = useState<SavedQuery[]>([
    {
      id: "1",
      name: "Daily Active Check",
      query: "Show all types with active codes that are currently effective",
      preview: "Show all types with active codes...",
    },
  ]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const file = e.target.files[0];
      console.log("Selected file:", file.name);
    }
  };

  const handleInterpret = async () => {
    if (!query.trim()) return;

    setIsProcessing(true);
    try {
      const impl =
        provider === "NLPS_API"
          ? httpNlpProvider
          : provider === "OpenAI"
            ? httpOpenAiProvider
            : nlpProvider;
      const intent = await impl.interpret(query);
      setCurrentIntent(intent);
      setCurrentResult(null);
    } catch (error) {
      console.error("Error interpreting query:", error);
      setCurrentIntent(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRunQuery = async (dryRun = false) => {
    if (!query.trim()) return;

    setIsProcessing(true);
    try {
      const impl = provider === "NLPS_API" ? httpNlpProvider : provider === "OpenAI" ? httpOpenAiProvider : nlpProvider;
      const intent = currentIntent || (await impl.interpret(query));
      console.log("intent", intent)
      const result = await impl.run(query, { dryRun });

      setCurrentIntent(intent);
      setCurrentResult(result);

      // If executed (not dry run) and succeeded, refresh caches so Tree/Managers update immediately
      if (!dryRun && result?.success) {
        try {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["types"] }),
            queryClient.invalidateQueries({ queryKey: ["codes"] }),
            queryClient.invalidateQueries({ queryKey: ["typeEdges"] }),
            queryClient.invalidateQueries({ queryKey: ["stats"] }),
          ]);
        } catch { }
      }

      // Add to history
      const historyItem: QueryHistoryItem = {
        id: Date.now().toString(),
        query,
        intent,
        result,
        timestamp: new Date().toISOString(),
      };

      setQueryHistory((prev) => [historyItem, ...prev.slice(0, 9)]); // Keep last 10
    } catch (error) {
      console.error("Error running query:", error);
      setCurrentResult({
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const { setBreadcrumbs } = useBreadcrumbs();

  useEffect(() => {
    setBreadcrumbs([
      {
        label: `AI Console`,
        path: `/ai-builder`,
      },
    ]);
  }, []);

  const handleSaveQuery = () => {
    if (!query.trim()) return;

    const name = window.prompt("Enter a name for this query:");
    if (!name) return;

    const savedQuery: SavedQuery = {
      id: Date.now().toString(),
      name,
      query,
      preview: query.length > 50 ? query.substring(0, 47) + "..." : query,
    };

    setSavedQueries((prev) => [...prev, savedQuery]);
  };

  const handleLoadSavedQuery = (savedQuery: SavedQuery) => {
    setQuery(savedQuery.query);
    setCurrentIntent(null);
    setCurrentResult(null);
  };

  const handleDeleteSavedQuery = (id: string) => {
    setSavedQueries((prev) => prev.filter((q) => q.id !== id));
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours} hours ago`;
    return date.toLocaleDateString();
  };
  console.log("queryHistory", queryHistory)
  return (
    <div className="bg-gray-50 min-h-full">
      <div className="mb-4 md:mb-6">
        <h2 className="text-xl md:text-xl font-semibold text-gray-900">
          AI / NLPS Console
        </h2>
        <p className="text-gray-600 mt-1 text-md md:text-base">
          Natural language processing for data operations
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 md:gap-6">
        {/* Query Interface */}
        <div className="xl:col-span-2">
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="mb-4">
                <label className="block text-lg font-medium font-semibold mb-2">
                  Natural Language Query
                </label>
                <Textarea
                  rows={3}
                  placeholder="Ask me to find, create, update, or delete types and codes..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="resize-none"
                />
              </div>

              <div className="flex items-center space-x-3 mb-6">
                <Button
                  onClick={() => handleRunQuery(false)}
                  disabled={isProcessing || !query.trim()}
                  className="bg-workfinity-500 hover:bg-workfinity-600"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Run Query
                </Button>
                <Button
                  onClick={() => handleRunQuery(true)}
                  disabled={isProcessing || !query.trim()}
                  variant="outline"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Dry Run
                </Button>
                <Button
                  onClick={handleInterpret}
                  disabled={isProcessing || !query.trim()}
                  variant="outline"
                >
                  <Bot className="h-4 w-4 mr-2" />
                  Interpret
                </Button>
                <Button
                  onClick={handleSaveQuery}
                  disabled={!query.trim()}
                  variant="outline"
                  size="sm"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
                {/* <div className="flex items-center space-x-2 ml-auto">
                  <label className="text-sm text-gray-600">Provider:</label>
                  <Select value={provider} onValueChange={setProvider}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LocalRules">Local Rules</SelectItem>
                      <SelectItem value="NLPS_API">NLPS API</SelectItem>
                      <SelectItem value="OpenAI">OpenAI</SelectItem>
                    </SelectContent>
                  </Select>
                </div> */}

                {/* Add file upload button */}
                {/* <div className="relative">
                  <input
                    type="file"
                    id="fileUpload"
                    className="hidden"
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                    onChange={(e) => {
                      if (e.target.files?.length) {
                        const file = e.target.files[0];
                        console.log("Selected file:", file.name);
                      }
                    }}
                  />
                  <label htmlFor="fileUpload">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="cursor-pointer"
                    >
                      <span className="mr-2">+</span> Add File
                    </Button>
                  </label>
                </div> */}

                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                    onChange={handleChange}
                  />
                  <Button onClick={handleClick} variant="outline" size="sm">
                    <span className="mr-2">+</span> Add File
                  </Button>
                </div>
              </div>

              {/* Interpretation Panel */}
              {currentIntent && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    Query Interpretation
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-blue-700 font-medium">Intent:</span>
                      <Badge className="bg-blue-100 text-blue-800">
                        {currentIntent.intent}
                      </Badge>
                      <span className="text-blue-700 font-medium ml-4">
                        Confidence:
                      </span>
                      <span className="text-blue-800 font-mono">
                        {Math.round(currentIntent.confidence * 100)}%
                      </span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">
                        Entities:
                      </span>
                      <div className="mt-1 space-x-2">
                        {Object.entries(currentIntent.entities).map(
                          ([key, value]) => (
                            <Badge
                              key={key}
                              variant="outline"
                              className="bg-blue-100 text-blue-800 text-xs"
                            >
                              {key}: {String(value)}
                            </Badge>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Results Panel */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Query Results
                </h4>
                {currentResult ? (
                  <div className="space-y-2">
                    {currentResult.success ? (
                      <div className="text-sm">
                        <div className="flex items-center space-x-2 mb-2">
                          <Badge className="bg-green-100 text-green-800">
                            Success
                          </Badge>
                          {currentResult.affectedRows && (
                            <span className="text-gray-600">
                              {currentResult.affectedRows} rows affected
                            </span>
                          )}
                        </div>
                        <div className="bg-white p-3 rounded border text-gray-700">
                          {typeof currentResult.data === "string" ? (
                            currentResult.data
                          ) : (
                            <pre className="text-xs overflow-auto">
                              {JSON.stringify(currentResult.data, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm">
                        <Badge className="bg-red-100 text-red-800 mb-2">
                          Error
                        </Badge>
                        <div className="bg-red-50 border border-red-200 p-3 rounded text-red-700">
                          {currentResult.error}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 text-center py-4">
                    {isProcessing
                      ? "Processing query..."
                      : "Run a query to see results"}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* History & Saved Queries */}
        <div className="space-y-6">
          {/* Query History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5" />
                <span>Query History</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-64">
                <div className="p-4 space-y-3">
                  {queryHistory.length > 0 ? (
                    queryHistory.map((item) => (
                      <div
                        key={item.id}
                        className="text-sm p-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => setQuery(item.query)}
                      >
                        <div className="font-medium text-gray-900 mb-1 line-clamp-2">
                          {item.query}
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <Badge variant="outline" className="text-xs">
                            {item.intent.intent}
                          </Badge>
                          <span>{formatTimestamp(item.timestamp)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 text-center py-8">
                      No query history yet
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Saved Queries */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Bookmark className="h-5 w-5" />
                <span>Saved Queries</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {savedQueries.map((savedQuery) => (
                <div
                  key={savedQuery.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded"
                >
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => handleLoadSavedQuery(savedQuery)}
                  >
                    <div className="text-sm font-medium text-gray-900">
                      {savedQuery.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {savedQuery.preview}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteSavedQuery(savedQuery.id)}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
